/* ================================================================
   BRITTEN CUSTOM: Dress match core — the engine without the page
   ================================================================
   Added:       September 2026
   Author:      Claude / Matthew
   Location:    assets/britten-dress-match-core.js
   Loaded by:   snippets/britten-dress-match.liquid (defer, BEFORE
                assets/britten-dress-match.js) and by any other page that
                wants the same dress reading + matching (the Dress Finder).

   PURPOSE:
   Everything about "read a photo of a dress, match it against the bride
   index" that does not touch the page. No DOM ids, no i18n strings, no
   markup: a page (the Real Brides block, the Dress Finder) owns its own
   interface and drives this. Classic script, vanilla IIFE, no build step.
   Defines window.BrittenDressMatchCore. If this file is missing (or
   FEATURE_ENABLED is false) the pages that need it stay hidden, silently.

   API — var core = BrittenDressMatchCore.create({
            indexUrl, scoreUrl, endpoint, turnstileSitekey, debug });
     core.probeIndex()      -> Promise<boolean>. Cheap existence check for
                               the bride index: HEAD, falling back to a
                               1-byte ranged GET. Never downloads the index.
     core.loadEngine()      -> Promise<{ index, api }>. Memoised. Fetches the
                               index JSON and the scoring script (api =
                               window.BrittenDressMatch). Rejects with
                               err.code = 'no_index' | 'no_score' |
                               'empty_index'.
     core.readPhoto(file, hooks?)
                            -> Promise<result>. HEIC fallback (heic2any,
                               lazy), downscale to a 1200px JPEG q0.85 via
                               createImageBitmap + canvas (this also strips
                               EXIF/location), best-effort Turnstile, POST
                               with a 15s timeout, generation counter so a
                               slow older answer can never beat a newer one.
                               result = { ok: true, tags, coverage, view }
                                      | { ok: false, reason } where reason is
                               not_a_dress | unclear | busy | paused |
                               unavailable | too_large | bad_image |
                               timeout | superseded.
                               hooks (optional, both may be omitted):
                                 onPreview(dataUrl)  the downscaled JPEG, as
                                                     soon as it is ready
                                 onStatus('heic_converting')
                                                     a slow HEIC decode began
     core.cancel()          -> void. Abort what is in flight and bump the
                               generation (the pending readPhoto resolves
                               'superseded').
     core.previewUrl(file)  -> string. An object URL for a local preview, or
                               '' when the browser cannot show the file
                               (HEIC). core.revoke(url) releases it.
     core.rank(tags, opts)  -> ranked. core.groupByVeil(ranked, opts),
                               core.closestFirst(ranked, opts),
                               core.describe(tags) (profileDescription),
                               core.normalise(tags). All are inert (empty)
                               until loadEngine() has resolved.
     core.remember(tags) / core.recall() / core.forget()
                            -> sessionStorage 'britten.dressMatch.v1' =
                               { v: 1, tags, at }. Dress ATTRIBUTES only —
                               never the photo, never a data URL: values are
                               filtered to short vocabulary-style strings
                               before they are written and again when read.
                               recall() -> { tags, at } | null.
     core.emit(name, detail) -> the 'bdm:event' CustomEvent (and
                               Shopify.analytics 'dress_match_<name>'). The
                               payload carries rb_sid when analytics consent
                               allows one (assets/britten-rb-session.js, which
                               the page loads before this file; without it the
                               payload simply has no rb_sid).
     core.maxBytes          -> the upload size ceiling readPhoto enforces.

   The matching engine (assets/britten-dress-match-score.js) and the bride
   index (assets/britten-dress-match.json) are both written by the
   MarketingOS publish; neither lives in this repo. See MarketingOS
   design/DRESS-MATCH.md.

   CHANGELOG:
   - 29 Sep 2026: core.emit() adds rb_sid (from window.BrittenRb, when analytics
     consent allows one) to every event payload, so dress finder and dress match
     events can be joined to the order they led to.
   ================================================================ */
(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false;

  var debugRequested = false;
  function log(msg) {
    if ((DEBUG_MODE || debugRequested) && window.console) console.log('[Britten Dress Match Core] ' + msg);
  }

  if (!FEATURE_ENABLED) return;
  if (window.BrittenDressMatchCore) return; // already defined (script included twice)

  var DEFAULT_ENDPOINT = 'https://brittenmarketingos.netlify.app/.netlify/functions/dress-match';
  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
  var HEIC2ANY_SRC = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
  var MAX_BYTES = 25 * 1024 * 1024;
  var UPLOAD_TIMEOUT_MS = 15000;
  var MAX_EDGE = 1200;
  var JPEG_QUALITY = 0.85;
  var STORAGE_KEY = 'britten.dressMatch.v1';

  // Reasons the endpoint (or this file) may hand back. Anything else the
  // endpoint sends is treated as 'unavailable' — a calm, never-a-dead-end
  // answer for the page.
  var KNOWN_REASONS = {
    not_a_dress: true, unclear: true, busy: true, paused: true,
    unavailable: true, too_large: true, bad_image: true, timeout: true
  };

  // ---- small helpers ----
  function looksHeic(file) {
    return /heic|heif/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('script failed to load: ' + src)); };
      document.head.appendChild(s);
    });
  }

  function failWith(code, message) {
    var err = new Error(message || code);
    err.code = code;
    return err;
  }

  function has(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }

  // ---- what may be remembered ----
  // Field names look like `neckline`, `sleeve_fabric`; values like `a_line`,
  // `white_ivory`. Nothing longer or stranger than that is ever stored, so a
  // photo (a data: URL, a blob, a base64 string) cannot end up in storage
  // even by mistake. The photo-level fields describe the PHOTO, not the
  // dress, and are left out.
  var TAG_KEY_RE = /^[a-z][a-z0-9_]{0,31}$/;
  var TAG_VALUE_RE = /^[A-Za-z0-9_\- ]{1,48}$/;
  var PHOTO_LEVEL_KEYS = { is_wedding_dress: true, coverage: true, view: true };
  var MAX_EXTRAS = 24;

  function cleanTags(tags) {
    var out = {};
    if (!tags || typeof tags !== 'object') return out;
    Object.keys(tags).forEach(function (key) {
      if (!TAG_KEY_RE.test(key) || has(PHOTO_LEVEL_KEYS, key)) return;
      var value = tags[key];
      if (key === 'extras') {
        if (!Array.isArray(value)) return;
        var extras = value.filter(function (v) { return typeof v === 'string' && TAG_VALUE_RE.test(v); })
          .slice(0, MAX_EXTRAS);
        if (extras.length) out.extras = extras;
        return;
      }
      if (typeof value === 'string' && TAG_VALUE_RE.test(value)) out[key] = value;
    });
    return out;
  }

  function sessionStore() {
    try { return window.sessionStorage || null; } catch (e) { return null; }
  }

  function create(config) {
    config = config || {};
    if (config.debug) debugRequested = true;

    var indexUrl = config.indexUrl || '';
    var scoreUrl = config.scoreUrl || '';
    var endpoint = config.endpoint || DEFAULT_ENDPOINT;
    var turnstileSitekey = config.turnstileSitekey || '';

    var index = null;
    var api = null;
    var enginePromise = null;
    var turnstilePromise = null;
    var turnstileToken = '';
    var turnstileWidgetId = null;
    // Bumped on every readPhoto() and on cancel(), so a slow response from
    // an earlier photo can be told apart from the current one. activeController
    // is the in-flight endpoint fetch, so it can be aborted the moment it is
    // superseded rather than merely ignored.
    var generation = 0;
    var activeController = null;

    // ================================================================
    // Launch safety: a CHEAP existence check only — HEAD, or (where HEAD
    // isn't supported) a 1-byte ranged GET — never the full index.
    // ================================================================
    function probeRanged() {
      return fetch(indexUrl, {
        method: 'GET', credentials: 'omit', headers: { Range: 'bytes=0-0' }
      }).then(function (r) { return !!r.ok; }, function () { return false; });
    }

    function probeIndex() {
      if (!indexUrl || typeof fetch !== 'function') return Promise.resolve(false);
      return fetch(indexUrl, { method: 'HEAD', credentials: 'omit' }).then(function (r) {
        if (r.ok) return true;
        if (r.status === 405 || r.status === 501) return probeRanged();
        return false; // 404 etc.: no index yet
      }, function () {
        // Some static hosts mishandle HEAD outright rather than answering
        // 405 — give the ranged GET a try before settling for "no".
        return probeRanged();
      });
    }

    // ================================================================
    // Engine: the bride index + the scoring script
    // ================================================================
    function fetchIndex() {
      return fetch(indexUrl, { credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('index http ' + r.status);
        return r.json();
      }).catch(function (e) {
        throw failWith('no_index', (e && e.message) || 'index failed to load');
      });
    }

    function fetchScore() {
      if (window.BrittenDressMatch) return Promise.resolve();
      if (!scoreUrl) return Promise.reject(failWith('no_score', 'no score url'));
      return loadScript(scoreUrl).catch(function (e) {
        throw failWith('no_score', (e && e.message) || 'score failed to load');
      });
    }

    function loadEngine() {
      if (enginePromise) return enginePromise;
      enginePromise = Promise.all([fetchIndex(), fetchScore()]).then(function (results) {
        var data = results[0];
        var brides = (data && Array.isArray(data.brides)) ? data.brides : [];
        if (!brides.length) throw failWith('empty_index', 'the index has no brides');
        if (!window.BrittenDressMatch) throw failWith('no_score', 'the score script did not define the engine');
        index = data;
        api = window.BrittenDressMatch;
        log('engine ready (' + brides.length + ' brides)');
        return { index: index, api: api };
      });
      return enginePromise;
    }

    // ================================================================
    // Turnstile (lazy, never blocking)
    // ================================================================
    function ensureTurnstileScript() {
      if (turnstilePromise) return turnstilePromise;
      turnstilePromise = new Promise(function (resolve) {
        if (window.turnstile) { resolve(); return; }
        var s = document.createElement('script');
        s.src = TURNSTILE_SRC;
        s.async = true;
        s.onload = function () { resolve(); };
        s.onerror = function () { resolve(); }; // never block on it
        document.head.appendChild(s);
      });
      return turnstilePromise;
    }

    // Renders an invisible/interaction-only widget and resolves with a token
    // (or '' if it never turns up in time — the endpoint only enforces
    // Turnstile when TURNSTILE_SECRET_KEY is set server-side).
    function getTurnstileTokenBestEffort() {
      if (!turnstileSitekey) return Promise.resolve('');
      return ensureTurnstileScript().then(function () {
        return new Promise(function (resolve) {
          var settled = false;
          function done(token) { if (!settled) { settled = true; resolve(token || ''); } }
          setTimeout(function () { done(turnstileToken); }, 1200);
          if (!window.turnstile) { done(''); return; }
          if (turnstileWidgetId !== null) { done(turnstileToken); return; }
          var host = document.createElement('div');
          host.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden';
          document.body.appendChild(host);
          try {
            turnstileWidgetId = window.turnstile.render(host, {
              sitekey: turnstileSitekey,
              appearance: 'interaction-only',
              callback: function (token) { turnstileToken = token; done(token); },
              'error-callback': function () { done(''); }
            });
          } catch (e) { done(''); }
        });
      });
    }

    // ================================================================
    // File pipeline: HEIC convert (lazy) + downscale to a 1200px JPEG
    // (createImageBitmap + canvas — also strips EXIF/location).
    // ================================================================
    function heicFallback(file, hooks) {
      if (!looksHeic(file)) return Promise.reject(new Error('could not decode image'));
      if (hooks.onStatus) { try { hooks.onStatus('heic_converting'); } catch (e) { /* noop */ } }
      return loadScript(HEIC2ANY_SRC).then(function () {
        return window.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      }).then(function (converted) {
        var blob = Array.isArray(converted) ? converted[0] : converted;
        return createImageBitmap(blob, { imageOrientation: 'from-image' });
      });
    }

    function decodeToBitmap(file, hooks) {
      if (typeof createImageBitmap !== 'function') return heicFallback(file, hooks);
      return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () {
        return heicFallback(file, hooks);
      });
    }

    function drawAndExport(bitmap) {
      var w = bitmap.width, h = bitmap.height;
      var longEdge = Math.max(w, h);
      var scale = longEdge > MAX_EDGE ? (MAX_EDGE / longEdge) : 1;
      var cw = Math.max(1, Math.round(w * scale));
      var ch = Math.max(1, Math.round(h * scale));
      var canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, cw, ch);
      if (bitmap.close) bitmap.close();
      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (blob) {
          if (!blob) { reject(new Error('could not encode image')); return; }
          var reader = new FileReader();
          reader.onload = function () {
            var dataUrl = reader.result;
            resolve({ base64: String(dataUrl).split(',')[1] || '', dataUrl: dataUrl });
          };
          reader.onerror = function () { reject(new Error('could not read encoded image')); };
          reader.readAsDataURL(blob);
        }, 'image/jpeg', JPEG_QUALITY);
      });
    }

    function toJpegBase64(file, hooks) {
      return decodeToBitmap(file, hooks).then(drawAndExport);
    }

    // ================================================================
    // Upload -> endpoint
    // ================================================================
    // `controller` is created by readPhoto and kept in activeController, so a
    // second photo — or cancel() — can abort this exact fetch the moment it
    // is superseded, rather than merely ignoring a response still in flight.
    function postToEndpoint(base64, token, controller) {
      var timer = setTimeout(function () { if (controller) controller.abort(); }, UPLOAD_TIMEOUT_MS);
      var body = JSON.stringify({ image: base64, media_type: 'image/jpeg', turnstile: token || '' });
      var opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body };
      if (controller) opts.signal = controller.signal;
      return fetch(endpoint, opts).then(function (r) {
        clearTimeout(timer);
        return r.json().catch(function () { return { ok: false, reason: 'bad_image' }; });
      }, function (err) {
        clearTimeout(timer);
        var reason = (err && err.name === 'AbortError') ? 'timeout' : 'unavailable';
        return { ok: false, reason: reason };
      });
    }

    function abortActiveRequest() {
      if (activeController) {
        try { activeController.abort(); } catch (e) { /* noop */ }
        activeController = null;
      }
    }

    // What the endpoint said, as a result the page can act on. Anything that
    // is not a clear "yes, a dress" is a reason, never an exception.
    function interpret(resp) {
      if (!resp || resp.ok === false) {
        var reason = resp && resp.reason;
        return { ok: false, reason: has(KNOWN_REASONS, reason) ? reason : 'unavailable' };
      }
      if (resp.is_wedding_dress === false) return { ok: false, reason: 'not_a_dress' };
      return { ok: true, tags: resp.tags || {}, coverage: resp.coverage, view: resp.view };
    }

    var SUPERSEDED = { ok: false, reason: 'superseded' };

    // A second photo while the first is still in flight must never let the
    // OLDER response win. `generation` is bumped here and compared after
    // every async step; any step that finishes once a newer photo (or
    // cancel()) has started resolves 'superseded' instead of rendering.
    function readPhoto(file, hooks) {
      hooks = hooks || {};
      generation += 1;
      var mine = generation;
      abortActiveRequest();
      if (!file) return Promise.resolve({ ok: false, reason: 'bad_image' });
      if (file.size > MAX_BYTES) return Promise.resolve({ ok: false, reason: 'too_large' });
      ensureTurnstileScript();
      return toJpegBase64(file, hooks).then(function (img) {
        if (mine !== generation) return SUPERSEDED; // superseded while decoding
        if (hooks.onPreview) { try { hooks.onPreview(img.dataUrl); } catch (e) { /* noop */ } }
        return getTurnstileTokenBestEffort().then(function (token) {
          if (mine !== generation) return SUPERSEDED; // superseded while waiting on Turnstile
          var controller = ('AbortController' in window) ? new AbortController() : null;
          activeController = controller;
          return postToEndpoint(img.base64, token, controller).then(function (resp) {
            if (activeController === controller) activeController = null;
            if (mine !== generation) return SUPERSEDED; // a newer photo already landed
            var result = interpret(resp);
            log('readPhoto -> ' + (result.ok ? 'ok' : result.reason));
            return result;
          });
        });
      }, function () {
        if (mine !== generation) return SUPERSEDED;
        return { ok: false, reason: 'bad_image' };
      });
    }

    function cancel() {
      generation += 1;
      abortActiveRequest();
    }

    // ================================================================
    // Local preview (object URL)
    // ================================================================
    function previewUrl(file) {
      if (!file || looksHeic(file)) return ''; // browsers other than Safari cannot show HEIC
      try { return URL.createObjectURL(file); } catch (e) { return ''; }
    }

    function revoke(url) {
      if (!url) return;
      try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
    }

    // ================================================================
    // Matching (thin, synchronous — inert until loadEngine() has resolved)
    // ================================================================
    function normalise(tags) {
      if (!api || !index) return {};
      return api.normaliseTags(tags || {}, index);
    }

    function rank(tags, opts) {
      if (!api || !index) return [];
      return api.rank(api.normaliseTags(tags || {}, index), index, opts || {});
    }

    function groupByVeil(ranked, opts) {
      if (!api || !index) return [];
      return api.groupByVeil(ranked, index, opts || {});
    }

    function closestFirst(ranked, opts) {
      if (!api || !index) return [];
      return api.closestFirst(ranked, opts || {});
    }

    function describe(tags) {
      if (!api || !index) return [];
      return api.profileDescription(tags || {}, index);
    }

    // ================================================================
    // Remembered dress (attributes only — see cleanTags)
    // ================================================================
    function remember(tags) {
      var store = sessionStore();
      if (!store) return;
      var clean = cleanTags(tags);
      try {
        if (!Object.keys(clean).length) { store.removeItem(STORAGE_KEY); return; }
        store.setItem(STORAGE_KEY, JSON.stringify({ v: 1, tags: clean, at: Date.now() }));
      } catch (e) { /* storage full or blocked — remembering is a nicety */ }
    }

    function recall() {
      var store = sessionStore();
      if (!store) return null;
      try {
        var raw = store.getItem(STORAGE_KEY);
        if (!raw) return null;
        var data = JSON.parse(raw);
        if (!data || data.v !== 1) return null;
        var tags = cleanTags(data.tags);
        if (!Object.keys(tags).length) return null;
        return { tags: tags, at: typeof data.at === 'number' ? data.at : 0 };
      } catch (e) { return null; }
    }

    function forget() {
      var store = sessionStore();
      if (!store) return;
      try { store.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
    }

    // ================================================================
    // Analytics (no new vendors) — same event names/detail as ever
    // ================================================================
    function emit(name, detail) {
      var payload = { name: name };
      if (detail) {
        for (var k in detail) { if (has(detail, k)) payload[k] = detail[k]; }
      }
      try {
        var rbSid = window.BrittenRb && window.BrittenRb.sid ? window.BrittenRb.sid() : '';
        if (rbSid) payload.rb_sid = rbSid;
      } catch (e) { /* no id: the event goes without it */ }
      try { document.dispatchEvent(new CustomEvent('bdm:event', { detail: payload })); } catch (e) { /* noop */ }
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        try { window.Shopify.analytics.publish('dress_match_' + name, payload); } catch (e) { /* noop */ }
      }
    }

    return {
      maxBytes: MAX_BYTES,
      probeIndex: probeIndex,
      loadEngine: loadEngine,
      readPhoto: readPhoto,
      cancel: cancel,
      previewUrl: previewUrl,
      revoke: revoke,
      normalise: normalise,
      rank: rank,
      groupByVeil: groupByVeil,
      closestFirst: closestFirst,
      describe: describe,
      remember: remember,
      recall: recall,
      forget: forget,
      emit: emit
    };
  }

  window.BrittenDressMatchCore = { create: create };
}());
